/**
 * 后台。覆盖全部 16 个 admin.* action（含 admin.funnel 支付漏斗、admin.access.list 访问记录）。
 *
 * 认证靠 ADMIN_TOKEN（见 src/adminApi.ts），令牌只存 sessionStorage。
 * 后端对所有 admin action 都会先 requireAdmin()，令牌错会返回 403 ADMIN_REQUIRED。
 *
 * 「维护」里那几个动作会真实改动线上数据（清理、建索引、吊销会话），
 * 因此一律先 confirm 再执行，不做一键批量。
 */
import { useCallback, useEffect, useState } from "react";
import {
  callAdmin,
  getAdminToken,
  setAdminToken,
  REVIEW_STATUSES,
  REVIEW_LABEL,
  ORDER_STATUSES,
  ORDER_LABEL,
  type AdminItem,
  type AdminOrder,
  type AuditLog,
  type FunnelResult,
} from "../adminApi";

type Tab = "stats" | "funnel" | "records" | "hepan" | "orders" | "users" | "access" | "settings" | "maintenance";

const TABS: { key: Tab; label: string }[] = [
  { key: "stats", label: "概览" },
  { key: "funnel", label: "支付漏斗" },
  { key: "records", label: "排盘记录" },
  { key: "hepan", label: "合盘记录" },
  { key: "orders", label: "订单" },
  { key: "users", label: "用户" },
  { key: "access", label: "访问记录" },
  { key: "settings", label: "站点设置" },
  { key: "maintenance", label: "维护" },
];

// 概览卡片：营收类走单独格式化，普通计数直接展示。
const STAT_LABEL: Record<string, string> = {
  users: "用户",
  sessions: "会话",
  records: "排盘记录",
  hepanRecords: "合盘记录",
  messages: "留言",
  memberships: "会员",
  orders: "订单",
  paidOrders: "已支付订单",
  revenue: "累计营收",
  auditLogs: "访问记录",
};

const MONEY_STATS = new Set(["revenue"]);

function fmtMoney(value: number): string {
  return `¥${(value / 1).toLocaleString("zh-CN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function fmtUA(ua?: string): string {
  if (!ua) return "—";
  if (/iPhone|iPad|iOS/i.test(ua)) return "iOS";
  if (/Android/i.test(ua)) return "Android";
  if (/MicroMessenger/i.test(ua)) return "微信";
  if (/Windows/i.test(ua)) return "Windows";
  if (/Macintosh|Mac OS/i.test(ua)) return "Mac";
  if (/bot|spider|crawl/i.test(ua)) return "爬虫";
  return "其他";
}

export function AdminPage() {
  const [token, setToken] = useState(getAdminToken);
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState<Tab>("stats");
  const [error, setError] = useState("");

  const verify = useCallback(async (candidate: string) => {
    setAdminToken(candidate);
    const result = await callAdmin("admin.stats");
    if (result.ok) {
      setAuthed(true);
      setError("");
    } else {
      setAuthed(false);
      setAdminToken("");
      setError(result.error || "令牌校验失败");
    }
  }, []);

  useEffect(() => {
    if (getAdminToken()) void verify(getAdminToken());
  }, [verify]);

  if (!authed) {
    return (
      <main className="page admin-page">
        <h1>后台</h1>
        <form
          className="auth-form"
          onSubmit={(e) => {
            e.preventDefault();
            void verify(token.trim());
          }}
        >
          <label>
            管理员令牌
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              autoComplete="off"
              placeholder="ADMIN_TOKEN"
            />
          </label>
          <button type="submit">进入后台</button>
        </form>
        {error && <p className="form-error">{error}</p>}
        <p className="auth-hint">令牌仅保存在本标签页，关闭后失效。</p>
      </main>
    );
  }

  return (
    <main className="page admin-page">
      <div className="admin-head">
        <h1>后台</h1>
        <button
          type="button"
          className="link"
          onClick={() => {
            setAdminToken("");
            setAuthed(false);
            setToken("");
          }}
        >
          退出后台
        </button>
      </div>

      <div className="admin-tabs" role="tablist">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={tab === item.key}
            className={tab === item.key ? "active" : undefined}
            onClick={() => setTab(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === "stats" && <StatsPanel />}
      {tab === "funnel" && <FunnelPanel />}
      {tab === "records" && <ReviewPanel title="排盘记录" listAction="admin.records.list" reviewAction="admin.records.review" />}
      {tab === "hepan" && <ReviewPanel title="合盘记录" listAction="admin.hepan.list" reviewAction="admin.hepan.review" />}
      {tab === "orders" && <OrdersPanel />}
      {tab === "users" && <UsersPanel />}
      {tab === "access" && <AccessPanel />}
      {tab === "settings" && <SettingsPanel />}
      {tab === "maintenance" && <MaintenancePanel />}
    </main>
  );
}

function StatsPanel() {
  const [stats, setStats] = useState<Record<string, number> | null>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    callAdmin<{ stats?: Record<string, unknown> }>("admin.stats").then((result) => {
      if (!result.ok) {
        setMessage(result.error);
        return;
      }
      const source = (result.stats ?? {}) as Record<string, unknown>;
      const picked: Record<string, number> = {};
      for (const key of Object.keys(STAT_LABEL)) {
        const value = source[key];
        if (typeof value === "number") picked[key] = value;
      }
      setStats(picked);
    });
  }, []);

  if (message) return <p className="form-error">{message}</p>;
  if (!stats) return <p className="empty-state">加载中…</p>;

  return (
    <section className="admin-stats">
      {Object.entries(stats).map(([key, value]) => (
        <div key={key} className="stat-card">
          <span className="stat-value">{MONEY_STATS.has(key) ? fmtMoney(value) : value}</span>
          <span className="stat-label">{STAT_LABEL[key] ?? key}</span>
        </div>
      ))}
    </section>
  );
}

function FunnelPanel() {
  const [data, setData] = useState<FunnelResult | null>(null);
  const [message, setMessage] = useState("");
  const [days, setDays] = useState(30);

  useEffect(() => {
    callAdmin<FunnelResult>("admin.funnel", { days }).then((result) => {
      if (result.ok) {
        setData(result as FunnelResult);
        setMessage("");
      } else {
        setMessage(result.error);
      }
    });
  }, [days]);

  if (message) return <p className="form-error">{message}</p>;
  if (!data) return <p className="empty-state">加载中…</p>;

  const stages = data.stages ?? [];
  const revenue = data.revenue;
  const maxValue = Math.max(1, ...stages.map((s) => s.value));

  return (
    <section className="admin-funnel">
      <div className="funnel-range">
        <span>统计窗口</span>
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            type="button"
            className={days === d ? "active" : undefined}
            onClick={() => setDays(d)}
          >
            近{d}天
          </button>
        ))}
      </div>

      <div className="funnel-bars">
        {stages.map((stage, index) => {
          const prev = index > 0 ? stages[index - 1].value : stage.value;
          const step = prev > 0 ? Math.round((stage.value / prev) * 1000) / 10 : 0;
          return (
            <div key={stage.key} className="funnel-row">
              <div className="funnel-meta">
                <span className="funnel-label">{stage.label}</span>
                <span className="funnel-count">{stage.value}</span>
              </div>
              <div className="funnel-track">
                <div className="funnel-fill" style={{ width: `${Math.round((stage.value / maxValue) * 100)}%` }} />
              </div>
              <div className="funnel-rate">
                <span>占访客 {stage.rate}%</span>
                {index > 0 && <span className="funnel-step">↳ 上一环 {step}%</span>}
              </div>
            </div>
          );
        })}
      </div>

      {revenue && (
        <div className="admin-stats funnel-revenue">
          <div className="stat-card">
            <span className="stat-value">{fmtMoney(revenue.amount)}</span>
            <span className="stat-label">支付金额（{revenue.paidCount}笔）</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{fmtMoney(revenue.net)}</span>
            <span className="stat-label">净收入（扣退款）</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{fmtMoney(revenue.arpu)}</span>
            <span className="stat-label">客单价 ARPU</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{data.orders?.orderConversion ?? 0}%</span>
            <span className="stat-label">下单→支付转化</span>
          </div>
        </div>
      )}

      {data.orders && (
        <div className="funnel-orderstatus">
          {Object.entries(data.orders.byStatus).map(([status, count]) => (
            <span key={status} className="order-chip">
              {ORDER_LABEL[status] ?? status}：{count}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

function ReviewPanel({
  title,
  listAction,
  reviewAction,
}: {
  title: string;
  listAction: string;
  reviewAction: string;
}) {
  const [items, setItems] = useState<AdminItem[]>([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await callAdmin<{ items?: AdminItem[] }>(listAction, { limit: 100 });
    setLoading(false);
    if (result.ok) setItems(result.items ?? []);
    else setMessage(result.error);
  }, [listAction]);

  useEffect(() => {
    void load();
  }, [load]);

  async function review(item: AdminItem, status: string) {
    const result = await callAdmin(reviewAction, { _id: item._id, id: item.id, reviewStatus: status });
    if (result.ok) {
      setItems((prev) =>
        prev.map((row) => (row._id === item._id ? { ...row, reviewStatus: status } : row)),
      );
    } else {
      setMessage(result.error);
    }
  }

  if (loading) return <p className="empty-state">加载中…</p>;
  if (message) return <p className="form-error">{message}</p>;
  if (items.length === 0) return <p className="empty-state">暂无{title}。</p>;

  return (
    <table className="admin-table">
      <thead>
        <tr>
          <th scope="col">名称</th>
          <th scope="col">归属</th>
          <th scope="col">创建时间</th>
          <th scope="col">审核状态</th>
          <th scope="col">操作</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item._id ?? item.id}>
            <td>{item.name ?? item.title ?? "—"}</td>
            <td className="mono">{item.ownerId ?? "—"}</td>
            <td>{item.createdAt?.slice(0, 19).replace("T", " ") ?? "—"}</td>
            <td>
              {REVIEW_LABEL[item.reviewStatus ?? ""] ?? item.reviewStatus ?? "—"}
              {item.reviewReasons?.length ? (
                <span className="review-reason">{item.reviewReasons.join("、")}</span>
              ) : null}
            </td>
            <td className="admin-actions">
              {REVIEW_STATUSES.map((status) => (
                <button
                  key={status}
                  type="button"
                  disabled={item.reviewStatus === status}
                  onClick={() => review(item, status)}
                >
                  {REVIEW_LABEL[status]}
                </button>
              ))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function OrdersPanel() {
  const [items, setItems] = useState<AdminOrder[]>([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await callAdmin<{ items?: AdminOrder[] }>("admin.orders.list", { limit: 100 });
    setLoading(false);
    if (result.ok) setItems(result.items ?? []);
    else setMessage(result.error);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function update(item: AdminOrder, status: string) {
    if (!window.confirm(`把订单「${item.productName ?? item.id}」改为「${ORDER_LABEL[status]}」？`)) return;
    const result = await callAdmin("admin.orders.update", { id: item.id, _id: item._id, status });
    if (result.ok) setItems((prev) => prev.map((row) => (row._id === item._id ? { ...row, status } : row)));
    else setMessage(result.error);
  }

  if (loading) return <p className="empty-state">加载中…</p>;
  if (message) return <p className="form-error">{message}</p>;
  if (items.length === 0) return <p className="empty-state">暂无订单。</p>;

  return (
    <table className="admin-table">
      <thead>
        <tr>
          <th scope="col">订单号</th>
          <th scope="col">商品</th>
          <th scope="col">金额</th>
          <th scope="col">状态</th>
          <th scope="col">改为</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item._id ?? item.id}>
            <td className="mono">{item.id ?? "—"}</td>
            <td>{item.productName ?? "—"}</td>
            <td>
              {item.currency === "CNY" ? "¥" : ""}
              {item.amount ?? 0}
            </td>
            <td>{ORDER_LABEL[item.status ?? ""] ?? item.status ?? "—"}</td>
            <td className="admin-actions">
              {ORDER_STATUSES.filter((status) => status !== item.status).map((status) => (
                <button key={status} type="button" onClick={() => update(item, status)}>
                  {ORDER_LABEL[status]}
                </button>
              ))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function UsersPanel() {
  const [items, setItems] = useState<AdminItem[]>([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    callAdmin<{ items?: AdminItem[]; users?: AdminItem[] }>("admin.users.list", { limit: 100 }).then(
      (result) => {
        setLoading(false);
        if (result.ok) setItems(result.items ?? result.users ?? []);
        else setMessage(result.error);
      },
    );
  }, []);

  async function revoke(item: AdminItem) {
    const id = String(item.id ?? item.ownerId ?? "");
    if (!id) return;
    if (!window.confirm(`吊销用户 ${id} 的全部登录会话？该用户需重新登录。`)) return;
    const result = await callAdmin("admin.sessions.revoke", { ownerId: id, userId: id });
    setMessage(result.ok ? "已吊销该用户的会话。" : result.error);
  }

  if (loading) return <p className="empty-state">加载中…</p>;
  if (items.length === 0) return <p className="empty-state">{message || "暂无用户。"}</p>;

  return (
    <>
      {message && <p className="admin-message">{message}</p>}
      <table className="admin-table">
        <thead>
          <tr>
            <th scope="col">用户 ID</th>
            <th scope="col">账号</th>
            <th scope="col">登录方式</th>
            <th scope="col">注册时间</th>
            <th scope="col">操作</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item._id ?? item.id}>
              <td className="mono">{String(item.id ?? "—")}</td>
              <td>{String(item.accountMasked ?? item.name ?? "—")}</td>
              <td>{String(item.loginProvider ?? "—")}</td>
              <td>{String(item.registeredAt ?? "").slice(0, 19).replace("T", " ") || "—"}</td>
              <td className="admin-actions">
                <button type="button" onClick={() => revoke(item)}>
                  吊销会话
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

type AccessKind = "all" | "pageview" | "action";
const ACCESS_KINDS: { key: AccessKind; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "pageview", label: "页面浏览" },
  { key: "action", label: "仅操作" },
];

function AccessPanel() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [summary, setSummary] = useState<{ total: number; distinctIps: number; pageviews?: number } | null>(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState<AccessKind>("all");

  useEffect(() => {
    setLoading(true);
    callAdmin<{ accessLog?: AuditLog[]; summary?: { total: number; distinctIps: number; pageviews?: number } }>(
      "admin.access.list",
      { limit: 200, kind },
    ).then((result) => {
      setLoading(false);
      if (result.ok) {
        setLogs(result.accessLog ?? []);
        setSummary(result.summary ?? null);
        setMessage("");
      } else {
        setMessage(result.error);
      }
    });
  }, [kind]);

  const filterBar = (
    <div className="funnel-range access-filter">
      <span>筛选</span>
      {ACCESS_KINDS.map((item) => (
        <button
          key={item.key}
          type="button"
          className={kind === item.key ? "active" : undefined}
          onClick={() => setKind(item.key)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );

  if (loading) return <>{filterBar}<p className="empty-state">加载中…</p></>;
  if (message) return <>{filterBar}<p className="form-error">{message}</p></>;

  return (
    <>
      {filterBar}
      {summary && (
        <p className="access-summary">
          共 {summary.total} 条 · 独立 IP {summary.distinctIps} 个
          {typeof summary.pageviews === "number" && kind === "all" ? ` · 其中页面浏览 ${summary.pageviews} 条` : ""}
          <span className="access-note">（IP 仅改造后产生的记录才有，历史记录为空）</span>
        </p>
      )}
      {logs.length === 0 ? (
        <p className="empty-state">暂无记录。</p>
      ) : (
      <div className="table-scroll">
      <table className="admin-table access-table">
        <thead>
          <tr>
            <th scope="col">时间</th>
            <th scope="col">动作</th>
            <th scope="col">IP</th>
            <th scope="col">归属地</th>
            <th scope="col">设备</th>
            <th scope="col">用户</th>
            <th scope="col">详情</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log, index) => (
            <tr key={log._id ?? index}>
              <td>{log.createdAt?.slice(0, 19).replace("T", " ") ?? "—"}</td>
              <td className="mono">{log.action ?? "—"}</td>
              <td className="mono">{log.ip || "—"}</td>
              <td>{log.ipLocation || "—"}</td>
              <td title={log.userAgent}>{fmtUA(log.userAgent)}</td>
              <td className="mono">{log.ownerId ?? "—"}</td>
              <td className="audit-detail">
                {log.payload ? JSON.stringify(log.payload) : log.detail ? JSON.stringify(log.detail) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      )}
    </>
  );
}

type SiteSettings = {
  siteName?: string;
  announcement?: string;
  maintenance?: boolean;
  domain?: string;
  icpNumber?: string;
  mpsNumber?: string;
  companyName?: string;
  contactEmail?: string;
  contactPhone?: string;
};

/**
 * 站点设置。注意后端 updateSettings 的 siteName 默认值仍是旧名「滕云子八字」，
 * 而备案登记的网站名称是「好学文化」——保存时务必确认这一栏，
 * 网站名与备案不一致会被专项核查判定不合规。
 */
function SettingsPanel() {
  const [settings, setSettings] = useState<SiteSettings>({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    callAdmin<{ settings?: SiteSettings }>("settings.get").then((result) => {
      setLoading(false);
      if (result.ok) setSettings(result.settings ?? {});
      else setMessage(result.error);
    });
  }, []);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    const result = await callAdmin<{ settings?: SiteSettings }>("settings.update", { settings });
    setBusy(false);
    setFailed(!result.ok);
    setMessage(result.ok ? "已保存。" : result.error);
    if (result.ok && result.settings) setSettings(result.settings);
  }

  if (loading) return <p className="empty-state">加载中…</p>;

  const fields: { key: keyof SiteSettings; label: string; hint?: string }[] = [
    { key: "siteName", label: "网站名称", hint: "必须与工信部备案登记一致（好学文化）" },
    { key: "companyName", label: "主办单位" },
    { key: "domain", label: "网站域名" },
    { key: "icpNumber", label: "ICP 备案号" },
    { key: "mpsNumber", label: "公安联网备案号" },
    { key: "contactEmail", label: "联系邮箱" },
    { key: "contactPhone", label: "联系电话", hint: "已于 2026-08-06 从前台移除，留空即可" },
    { key: "announcement", label: "站内公告" },
  ];

  return (
    <form className="admin-settings" onSubmit={save}>
      {fields.map((field) => (
        <label key={field.key}>
          {field.label}
          <input
            type="text"
            value={String(settings[field.key] ?? "")}
            onChange={(e) => setSettings({ ...settings, [field.key]: e.target.value })}
          />
          {field.hint && <small>{field.hint}</small>}
        </label>
      ))}
      <label className="admin-checkbox">
        <input
          type="checkbox"
          checked={Boolean(settings.maintenance)}
          onChange={(e) => setSettings({ ...settings, maintenance: e.target.checked })}
        />
        维护模式
      </label>
      <button type="submit" disabled={busy}>
        {busy ? "保存中…" : "保存设置"}
      </button>
      {message && <p className={failed ? "form-error" : "save-ok"}>{message}</p>}
    </form>
  );
}

function MaintenancePanel() {
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState("");

  async function run(action: string, label: string, warn?: string) {
    if (warn && !window.confirm(warn)) return;
    setBusy(action);
    setOutput("");
    const result = await callAdmin(action);
    setBusy("");
    setOutput(`【${label}】\n${JSON.stringify(result, null, 2)}`);
  }

  const jobs: { action: string; label: string; desc: string; warn?: string }[] = [
    { action: "admin.diagnostics", label: "环境自检", desc: "检查数据库连接、集合、环境变量配置状态。只读。" },
    { action: "admin.report.generate", label: "上线检查报告", desc: "生成主体、备案、内容、支付等就绪项报告。只读。" },
    {
      action: "admin.indexes.ensure",
      label: "建立索引",
      desc: "为各集合补建查询索引。",
      warn: "将对数据库执行建索引操作，数据量大时可能短暂占用资源。继续？",
    },
    {
      action: "admin.maintenance.cleanup",
      label: "清理过期数据",
      desc: "清理过期验证码、失效会话等。",
      warn: "将删除过期验证码与失效会话，不可撤销。继续？",
    },
  ];

  return (
    <section className="admin-maintenance">
      {jobs.map((job) => (
        <div key={job.action} className="maintenance-job">
          <div>
            <strong>{job.label}</strong>
            <p>{job.desc}</p>
          </div>
          <button
            type="button"
            disabled={busy === job.action}
            onClick={() => run(job.action, job.label, job.warn)}
          >
            {busy === job.action ? "执行中…" : "执行"}
          </button>
        </div>
      ))}
      {output && <pre className="maintenance-output">{output}</pre>}
    </section>
  );
}
